import { InfoOutlined } from '@mui/icons-material';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SegmentCard from 'components/data-card/variants/segment-card/segment-card.component';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import MovementLegendDrawer from 'components/movement-legend-drawer/movement-legend-drawer.component';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  SegmentCell,
  SegmentCellAction,
  SegmentGridVariant,
  SegmentMetric,
  SegmentTier,
} from './segment-grid.types';

/**
 * Derive the quick-action payload from a cell's grid position. Rows render
 * highest spend first (row 0 = spend tier 3); columns are visit tier 1→3.
 */
const buildCellAction = (
  rowIndex: number,
  colIndex: number,
  segmentName: string,
): SegmentCellAction => {
  const spendTier = (3 - rowIndex) as 1 | 2 | 3;
  const visitTier = (colIndex + 1) as 1 | 2 | 3;
  return {
    stageKey: `SPEND_TIER_${spendTier}_VISIT_TIER_${visitTier}`,
    spendTier,
    visitTier,
    segmentName,
  };
};

/** The per-cell quick-create action button, overlaid top-right of a cell. */
const CellActionButton = ({
  rowIndex,
  colIndex,
  segmentName,
  onCellAction,
  dataTestId,
}: {
  rowIndex: number;
  colIndex: number;
  segmentName: string;
  onCellAction: (action: SegmentCellAction) => void;
  dataTestId: string;
}) => {
  const { t } = useTranslation();
  const label = t('segmentGrid.action.createCommunication', {
    segment: segmentName,
  });
  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={() =>
          onCellAction(buildCellAction(rowIndex, colIndex, segmentName))
        }
        sx={{
          position: 'absolute',
          bottom: 4,
          right: 4,
          zIndex: 1,
          // Solid white so the action reads on any cell background (active
          // blue, sliders amber, churn pink, dark mode).
          bgcolor: 'common.white',
          color: 'grey.800',
          boxShadow: 1,
          '&:hover': { bgcolor: 'grey.100' },
        }}
        data-testid={`${dataTestId}.Action`}
      >
        <LocalOfferOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export type SegmentGridProps = {
  /**
   * 3 rows. Highest spend first (e.g. Indulgent → Core → Value), so the grid
   * renders top-down from highest tier.
   */
  spendTiers: SegmentTier[];
  /**
   * 3 columns. Lowest visit count first (e.g. Explorer → Regular → Superfan).
   */
  visitTiers: SegmentTier[];
  /**
   * 9 display-ready cells in canonical `SPEND_TIER_x_VISIT_TIER_y` order
   * (spend-major, tier1→tier3). The grid maps array index → row/column; the
   * consumer selects which metric's cells to pass.
   */
  cells: SegmentCell[];
  metric?: SegmentMetric;
  onMetricChange?: (metric: SegmentMetric) => void;
  /**
   * When true, shows the metric toggle (Count / Spend / Visits) above the grid.
   * Off for Churn and Settings preview.
   */
  showMetricToggle?: boolean;
  /**
   * When true, shows the per-cell movement badge.
   * Off for Churn and Settings preview.
   */
  showMovementBadge?: boolean;
  /**
   * Visual variant — controls cell background. Default = white, sliders = amber,
   * churn = pink/red.
   */
  variant?: SegmentGridVariant;
  /**
   * When true, shows an ⓘ button beside the title that opens the movement
   * legend reference drawer; per-cell movement badges become clickable and
   * open the same drawer.
   */
  showMovementLegendButton?: boolean;
  /**
   * When provided, each cell shows a quick-action button that fires this with the
   * cell's derived tiers + stage key (sc-365239). Omit to hide the action.
   */
  onCellAction?: (action: SegmentCellAction) => void;
  title?: string;
  loading?: boolean;
  'data-testid'?: string;
};

// Spend is money: always render two decimals (e.g. "$19.00", "$19.01"). The
// `$` prefix lives in the i18n template, so pass the formatted number only.
const money = (value: number): string => value.toFixed(2);

const formatSpendRange = (tier: SegmentTier, t: TFunction): string => {
  if (tier.thresholdMin == null) return '';
  if (tier.thresholdMax == null) {
    return t('segmentGrid.spendTier.rangeAtLeast', {
      min: money(tier.thresholdMin),
    });
  }
  return t('segmentGrid.spendTier.rangeBetween', {
    min: money(tier.thresholdMin),
    max: money(tier.thresholdMax),
  });
};

const formatVisitsRange = (tier: SegmentTier, t: TFunction): string => {
  if (tier.thresholdMin == null) return '';
  if (tier.thresholdMax == null) {
    return t('segmentGrid.visitTier.rangeAtLeast', { min: tier.thresholdMin });
  }
  return t('segmentGrid.visitTier.rangeBetween', {
    min: tier.thresholdMin,
    max: tier.thresholdMax,
  });
};

/**
 * Maps a display position to the canonical cell index. Rows render highest
 * spend first (row 0 = spend tier3) while the cells array is spend-major
 * tier1→tier3, so the spend axis is flipped; the visit axis (columns) already
 * matches tier1→tier3.
 */
const cellAt = (
  cells: SegmentCell[],
  rowIndex: number,
  colIndex: number,
): SegmentCell | undefined => cells[(2 - rowIndex) * 3 + colIndex];

/**
 * The grid body without the surrounding `Card`. Use when the grid already sits
 * inside a card (e.g. the Settings preview); otherwise prefer the default
 * `SegmentGrid` export.
 */
export const SegmentGridBase = ({
  spendTiers,
  visitTiers,
  cells,
  metric = 'count',
  onMetricChange,
  showMetricToggle = false,
  showMovementBadge = false,
  variant = 'default',
  showMovementLegendButton = false,
  onCellAction,
  title,
  loading = false,
  'data-testid': dataTestId = 'SegmentGrid',
}: SegmentGridProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const legendDrawer = useDrawer();

  const onMovementClick = showMovementLegendButton
    ? () => legendDrawer.open()
    : undefined;

  const handleMetricChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: SegmentMetric | null,
  ) => {
    if (next != null) onMetricChange?.(next);
  };

  return (
    <>
      <Stack spacing={2} height="100%" data-testid={dataTestId}>
        {(title || showMetricToggle) && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            flexWrap="wrap"
          >
            {title && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body1" component="div">
                  {title}
                </Typography>
                {showMovementLegendButton && (
                  <IconButton
                    size="small"
                    onClick={() => legendDrawer.open()}
                    aria-label={t('movementLegend.infoButtonLabel')}
                    data-testid={`${dataTestId}.MovementLegendButton`}
                  >
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            )}
            {showMetricToggle && (
              <ToggleButtonGroup
                value={metric}
                exclusive
                onChange={handleMetricChange}
                size="small"
                data-testid={`${dataTestId}.MetricToggle`}
              >
                <ToggleButton value="count">
                  {t('segmentGrid.metric.count')}
                </ToggleButton>
                <ToggleButton value="spend">
                  {t('segmentGrid.metric.spend')}
                </ToggleButton>
                <ToggleButton value="visits">
                  {t('segmentGrid.metric.visits')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
        )}

        {isXs ? (
          <StackedLayout
            spendTiers={spendTiers}
            visitTiers={visitTiers}
            cells={cells}
            showMovementBadge={showMovementBadge}
            variant={variant}
            loading={loading}
            dataTestId={dataTestId}
            onMovementClick={onMovementClick}
            onCellAction={onCellAction}
          />
        ) : (
          <DesktopLayout
            spendTiers={spendTiers}
            visitTiers={visitTiers}
            cells={cells}
            showMovementBadge={showMovementBadge}
            variant={variant}
            loading={loading}
            dataTestId={dataTestId}
            onMovementClick={onMovementClick}
            onCellAction={onCellAction}
          />
        )}
      </Stack>
      {showMovementLegendButton && <MovementLegendDrawer {...legendDrawer} />}
    </>
  );
};

/**
 * Standalone segment grid: `SegmentGridBase` wrapped in a full-height `Card`.
 * Used by the report tabs (Active / Sliders / Churn). The `data-testid` lands
 * on the grid body inside the card, so existing selectors keep working.
 */
const SegmentGrid = (props: SegmentGridProps) => (
  <Card
    sx={{ height: '100%' }}
    data-testid={`${props['data-testid'] ?? 'SegmentGrid'}.Card`}
  >
    <CardContent sx={{ height: '100%' }}>
      <SegmentGridBase {...props} />
    </CardContent>
  </Card>
);

type LayoutProps = {
  spendTiers: SegmentTier[];
  visitTiers: SegmentTier[];
  cells: SegmentCell[];
  showMovementBadge: boolean;
  variant: SegmentGridVariant;
  loading: boolean;
  dataTestId: string;
  onMovementClick?: () => void;
  onCellAction?: (action: SegmentCellAction) => void;
};

const DesktopLayout = ({
  spendTiers,
  visitTiers,
  cells,
  showMovementBadge,
  variant,
  loading,
  dataTestId,
  onMovementClick,
  onCellAction,
}: LayoutProps) => {
  const { t } = useTranslation();

  return (
    <Box
      display="grid"
      gridTemplateColumns="auto repeat(3, 1fr)"
      gridTemplateRows="repeat(3, 1fr) auto"
      gap={1}
      flex={1}
      alignSelf="center"
      pb={1}
    >
      {spendTiers.map((spendTier, rowIndex) => (
        <RowFragment
          key={`row-${rowIndex}`}
          spendTier={spendTier}
          spendRange={formatSpendRange(spendTier, t)}
          rowIndex={rowIndex}
          visitTiers={visitTiers}
          cells={cells}
          showMovementBadge={showMovementBadge}
          variant={variant}
          loading={loading}
          dataTestId={dataTestId}
          onMovementClick={onMovementClick}
          onCellAction={onCellAction}
        />
      ))}

      <Box />

      {visitTiers.map((tier, colIndex) => {
        const range = formatVisitsRange(tier, t);
        return (
          <Box
            key={`visit-header-${colIndex}`}
            textAlign="center"
            data-testid={`${dataTestId}.VisitHeader.${colIndex}`}
          >
            <Typography variant="subtitle2">{tier.name}</Typography>
            {range && (
              <Typography variant="caption" color="text.secondary">
                {range}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const StackedLayout = ({
  spendTiers,
  visitTiers,
  cells,
  showMovementBadge,
  variant,
  loading,
  dataTestId,
  onMovementClick,
  onCellAction,
}: LayoutProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} flex={1}>
      {visitTiers.map((visitTier, colIndex) => {
        const visitRange = formatVisitsRange(visitTier, t);
        return (
          <Stack key={`section-${colIndex}`} spacing={1}>
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={1}
              data-testid={`${dataTestId}.VisitHeader.${colIndex}`}
            >
              <Typography variant="subtitle1">{visitTier.name}</Typography>
              {visitRange && (
                <Typography variant="caption" color="text.secondary">
                  {visitRange}
                </Typography>
              )}
            </Stack>
            <Stack spacing={1}>
              {spendTiers.map((spendTier, rowIndex) => {
                const cell = cellAt(cells, rowIndex, colIndex);
                const spendRange = formatSpendRange(spendTier, t);
                const segmentName = `${spendTier.name} ${visitTier.name}`;
                return (
                  <Box
                    key={`cell-${rowIndex}-${colIndex}`}
                    position="relative"
                    sx={{
                      height: '100%',
                      '& > .MuiCard-root': { height: '100%' },
                    }}
                  >
                    <SegmentCard
                      data-testid={`${dataTestId}.Cell.${rowIndex}.${colIndex}`}
                      value={cell?.value}
                      variant={variant}
                      loading={loading}
                      movement={showMovementBadge ? cell?.movement : undefined}
                      onMovementClick={onMovementClick}
                      slots={{
                        title: (
                          <Stack spacing={0}>
                            <Typography
                              variant="h3"
                              component="div"
                              // `&&` doubles the class specificity so this wins
                              // over the h3 variant's responsiveFontSizes @media
                              // rules (which otherwise keep it at ~17px).
                              sx={{
                                fontWeight: 500,
                                '&&': { fontSize: '15px' },
                              }}
                            >
                              {cell?.title ?? segmentName}
                            </Typography>
                            {spendRange && (
                              <Typography
                                variant="caption"
                                component="div"
                                color="text.secondary"
                              >
                                {spendRange}
                              </Typography>
                            )}
                          </Stack>
                        ),
                      }}
                    />
                    {onCellAction && (
                      <CellActionButton
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                        segmentName={segmentName}
                        onCellAction={onCellAction}
                        dataTestId={`${dataTestId}.Cell.${rowIndex}.${colIndex}`}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

type RowFragmentProps = {
  spendTier: SegmentTier;
  spendRange: string;
  rowIndex: number;
  visitTiers: SegmentTier[];
  cells: SegmentCell[];
  showMovementBadge: boolean;
  variant: SegmentGridVariant;
  loading: boolean;
  dataTestId: string;
  onMovementClick?: () => void;
  onCellAction?: (action: SegmentCellAction) => void;
};

const RowFragment = ({
  spendTier,
  spendRange,
  rowIndex,
  visitTiers,
  cells,
  showMovementBadge,
  variant,
  loading,
  dataTestId,
  onMovementClick,
  onCellAction,
}: RowFragmentProps) => {
  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        paddingRight={1}
        data-testid={`${dataTestId}.SpendHeader.${rowIndex}`}
      >
        <Typography variant="subtitle2">{spendTier.name}</Typography>
        {spendRange && (
          <Typography variant="caption" color="text.secondary">
            {spendRange}
          </Typography>
        )}
      </Box>
      {visitTiers.map((visitTier, colIndex) => {
        const cell = cellAt(cells, rowIndex, colIndex);
        const segmentName = `${spendTier.name} ${visitTier.name}`;
        return (
          <Box
            key={`cell-${rowIndex}-${colIndex}`}
            position="relative"
            sx={{ height: '100%', '& > .MuiCard-root': { height: '100%' } }}
          >
            <SegmentCard
              data-testid={`${dataTestId}.Cell.${rowIndex}.${colIndex}`}
              title={cell?.title ?? segmentName}
              value={cell?.value}
              variant={variant}
              loading={loading}
              movement={showMovementBadge ? cell?.movement : undefined}
              onMovementClick={onMovementClick}
              slotProps={{
                title: { sx: { fontWeight: 500, '&&': { fontSize: '15px' } } },
              }}
            />
            {onCellAction && (
              <CellActionButton
                rowIndex={rowIndex}
                colIndex={colIndex}
                segmentName={segmentName}
                onCellAction={onCellAction}
                dataTestId={`${dataTestId}.Cell.${rowIndex}.${colIndex}`}
              />
            )}
          </Box>
        );
      })}
    </>
  );
};

export default SegmentGrid;
